import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SNAPSHOT } from "./data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative bg-[#F2EDE1] pt-40 pb-24 md:pt-48 md:pb-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-[1.15fr_1fr] gap-16 lg:gap-10 items-center">
        {/* Left */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="eyebrow text-[#0B3D2E] flex items-center gap-4 mb-10"
            data-testid="hero-eyebrow"
          >
            <span className="w-8 h-px bg-[#A6874E]" />
            Growth Through Strategy
          </motion.p>

          <h1 className="font-grotesk font-bold text-[#0B3D2E] leading-[1.02] tracking-tight" data-testid="hero-heading">
            <span className="block overflow-hidden">
              <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block text-5xl sm:text-6xl md:text-7xl">
                Make your
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-5xl sm:text-6xl md:text-7xl">
                next move
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block text-5xl sm:text-6xl md:text-7xl">
                <em className="font-display italic font-medium text-[#A6874E]">matter.</em>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 max-w-md text-sm md:text-base leading-relaxed text-[#0B3D2E]/70"
            data-testid="hero-subcopy"
          >
            Elevora brings strategy, creative and digital execution together to help
            ambitious businesses move forward with clarity.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group flex items-center gap-3 bg-[#0B3D2E] text-[#F2EDE1] px-7 py-4 eyebrow hover:bg-[#A6874E] transition-colors duration-300"
            >
              Let's Talk About Growth
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#work"
              data-testid="hero-cta-secondary"
              className="flex items-center gap-3 border border-[#0B3D2E]/30 text-[#0B3D2E] px-7 py-4 eyebrow hover:border-[#0B3D2E] hover:bg-[#0B3D2E] hover:text-[#F2EDE1] transition-colors duration-300"
            >
              Explore Case Studies
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-14 text-[11px] tracking-[0.15em] text-[#0B3D2E]/50"
            data-testid="hero-footer-line"
          >
            Thoughtful partnership. Clear priorities. Work with purpose.
          </motion.p>
        </div>

        {/* Right — snapshot card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:justify-self-end w-full max-w-lg"
          data-testid="hero-snapshot"
        >
          <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full border border-dashed border-[#A6874E]/50 pointer-events-none" />
          <div className="relative bg-[#FCFAF4] rounded-2xl shadow-[0_30px_60px_-20px_rgba(11,61,46,0.25)] p-8 md:p-10">
            <div className="flex items-center justify-between mb-8">
              <span className="eyebrow text-[#0B3D2E]">Elevora / Snapshot</span>
              <span className="eyebrow text-[#0B3D2E] flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0B3D2E]" />
                Built Around Growth
              </span>
            </div>
            <div>
              {SNAPSHOT.map((row, i) => (
                <div
                  key={row.label}
                  className={`flex items-center justify-between py-5 ${
                    i > 0 ? "border-t border-[#0B3D2E]/10" : ""
                  }`}
                  data-testid={`snapshot-row-${i}`}
                >
                  <span className="text-sm text-[#0B3D2E]/70">{row.label}</span>
                  <span className="font-grotesk font-bold text-3xl text-[#0B3D2E]">{row.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-[#0B3D2E] rounded-xl px-5 py-4 flex items-center gap-4">
              <span className="w-8 h-8 rounded-full bg-[#A6874E] flex items-center justify-center shrink-0">
                <svg viewBox="0 0 32 32" className="w-4 h-4 text-[#0B3D2E]" fill="currentColor" aria-hidden="true">
                  <path d="M6 4h18l-3 4H11l-2 3.5h11l-3 4H7L5 18v10h21v-4H11l8-11L28 4H6z" />
                </svg>
              </span>
              <span className="eyebrow text-[#F2EDE1]">Strategy · Creative · Digital Execution</span>
            </div>
          </div>
          <p className="mt-6 text-right text-[11px] leading-relaxed text-[#0B3D2E]/50 max-w-xs ml-auto">
            A considered mix of ideas and execution, shaped around your next step.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
